import type { ReactNode } from "react";
import type { Theme } from "@mui/material/styles";
import { Box, Button, Typography, useTheme } from "@mui/material";
import { RequestTablePkIconSwitcher } from "../../RequestTable/RequestTablePkIconSwitcher";
import { DocumentListIcon, DownloadFileIcon, DownloadIcon } from "vig-ui-library";
import { RequestFormContext } from "../RequestFormContext";
import { RequestFieldDto } from "../Schemas/RequestFieldDto";
import { RequestParameterSectionDto } from "../Schemas/RequestParameterSectionDto";
import { RequestParameterTypeEnum } from "../Enums/RequestParameterTypeEnum";
import { RequestStateTypeEnum } from "../Enums/RequestStateTypeEnum";
import { Request } from "../Utilities/mappers";
import { downloadStreamFile } from "../Utilities/streamFileClient";

export type SubmittedRequestDetailViewProps = Omit<RequestFormContext, "pathToProp">;

/**
 * Request-type header chip background. Not defined on `pkpTheme` palette (closest is `info.main` #196E9B);
 * kept for parity with request table / Figma teal.
 */
const HEADER_ICON_BG = "#00838F";

/** Matches `pkpTheme.typography.body2` via MUI variant; leading-trim per design (limited browser support). */
const attachmentDownloadButtonSx = (theme: Theme) => ({
    flexShrink: 0,
    textTransform: "none" as const,
    typography: "body2",
    fontWeight: theme.typography.fontWeightRegular,
    fontStyle: "normal" as const,
    letterSpacing: 0,
    leadingTrim: "none" as const,
});

/** Outlined copy download — `body2` + medium weight; border `grey[200]` (pkp). */
const downloadCopyButtonSx = (theme: Theme) => ({
    typography: "body2",
    fontWeight: theme.typography.fontWeightMedium,
    fontStyle: "normal",
    letterSpacing: 0,
    leadingTrim: "none",
    textAlign: "center" as const,
    width: "fit-content",
    height: 40,
    borderRadius: "7px",
    borderWidth: 1,
    py: theme.spacing(1.125),
    px: theme.spacing(1.5),
    gap: 1,
    whiteSpace: "nowrap",
    flexShrink: 0,
    backgroundColor: theme.palette.common.white,
    color: theme.palette.common.black,
    borderColor: theme.palette.grey[200],
    "&:hover": {
        backgroundColor: theme.palette.common.white,
        borderColor: theme.palette.grey[200],
    },
});

/** Same mapping as `RequestStateIndicator.getColor` — warning / error (red) / success (green). */
function getRequestStateLabelColor(theme: Theme, requestStateTypeId: RequestStateTypeEnum | undefined | null): string {
    if (requestStateTypeId == null) {
        return theme.palette.text.primary;
    }

    const warning = theme.palette.warning.main;
    const error = theme.palette.error.main;
    const success = theme.palette.success.main;

    switch (requestStateTypeId) {
        case RequestStateTypeEnum.ROZPRACOVANA:
        case RequestStateTypeEnum.ULOZENA:
        case RequestStateTypeEnum.ODOSLANA_NA_SPRACOVANIE:
        case RequestStateTypeEnum.RIESI_SA:
        case RequestStateTypeEnum.CAKA_SA_NA_NIEKOHO:
        case RequestStateTypeEnum.ODOSLANA_NA_PODPIS:
        case RequestStateTypeEnum.LINK_ODOSLANY_EMAILOM:
        case RequestStateTypeEnum.LINK_ODOSLANY_SMS:
        case RequestStateTypeEnum.POTVRDENIE_ZRUSENE:
            return warning;
        case RequestStateTypeEnum.NEOPODSTATNENA:
        case RequestStateTypeEnum.ZRUSENA_A_NAHRADENA_INOU_CINNOSTOU:
            return error;
        case RequestStateTypeEnum.VYBAVENA:
        case RequestStateTypeEnum.VYBAVENA_A_POSLANA_NA_DALSIU_CINNOST:
        case RequestStateTypeEnum.ZIADOST_PODPISANA_RUCNE:
        case RequestStateTypeEnum.LINK_POTVRDENY_KLIENTOM:
            return success;
        default:
            return theme.palette.text.primary;
    }
}

/**
 * Grouping anchor for submitted content: nearest ancestor with non-empty `displayTitle`, else the topmost
 * section in the parent chain (so fields without a titled block still bucket together).
 */
function getSectionGroupAnchor(
    field: RequestFieldDto,
    sectionById: Map<string, RequestParameterSectionDto>,
): { groupKey: string; subheadline: string | null } {
    const visited = new Set<string>();
    let sid: string | undefined = field.parentSectionId;
    let topSectionId = field.parentSectionId;

    while (sid && !visited.has(sid)) {
        visited.add(sid);
        const section = sectionById.get(sid);
        if (!section) break;
        topSectionId = section.id;
        const t = section.displayTitle?.trim();
        if (t) {
            return { groupKey: section.id, subheadline: t };
        }
        const parentId = section.parentSectionId;
        sid = parentId != null && parentId !== "" ? parentId : undefined;
    }

    return { groupKey: topSectionId, subheadline: null };
}

type SubmittedContentFieldGroup = {
    groupKey: string;
    subheadline: string | null;
    fields: RequestFieldDto[];
};

/** Values that should not appear in the submitted summary (unfilled / placeholder). */
function isSubmittedFieldValueEmpty(displayValue: string): boolean {
    const t = displayValue.trim();
    return t === "" || t === "-";
}

/**
 * Radio groups are stored as one row per option in `fields`, but `groupValue` selects a single key.
 * Only the selected option should appear in the summary (otherwise every option shows the same value).
 */
function isRadioGroupRepresentativeField(field: RequestFieldDto, request: Request): boolean {
    if (field.type !== RequestParameterTypeEnum.RADIO_BUTTON || !field.group) {
        return true;
    }
    const group = request.itemsByGroup[field.group];
    if (!group) return true;
    const gv = group.groupValue;
    if (gv == null || gv === "") {
        return true;
    }
    return field.key === gv;
}

function buildSubmittedContentFieldGroups(
    fields: RequestFieldDto[],
    request: Request,
    sectionById: Map<string, RequestParameterSectionDto>,
): SubmittedContentFieldGroup[] {
    const groups: SubmittedContentFieldGroup[] = [];
    const groupIndexByKey = new Map<string, number>();

    for (const field of fields) {
        if (!isRadioGroupRepresentativeField(field, request)) continue;

        const value = getFieldDisplayValue(field, request);
        if (isSubmittedFieldValueEmpty(value)) continue;

        const anchor = getSectionGroupAnchor(field, sectionById);
        const existingIdx = groupIndexByKey.get(anchor.groupKey);
        if (existingIdx !== undefined) {
            groups[existingIdx].fields.push(field);
        } else {
            groupIndexByKey.set(anchor.groupKey, groups.length);
            groups.push({
                groupKey: anchor.groupKey,
                subheadline: anchor.subheadline,
                fields: [field],
            });
        }
    }

    return groups.filter((g) => g.fields.length > 0);
}

function getSubmittedFieldRowLabel(
    field: RequestFieldDto,
    request: Request,
    sectionById: Map<string, RequestParameterSectionDto>,
    groupHasNonRadioFields: boolean,
): string {
    if (
        field.type === RequestParameterTypeEnum.RADIO_BUTTON &&
        field.group &&
        request.itemsByGroup[field.group]
    ) {
        const anchor = getSectionGroupAnchor(field, sectionById);
        const st = anchor.subheadline?.trim();
        if (st && !groupHasNonRadioFields) {
            return st;
        }
    }
    return field.displayTitle || field.parameterText || field.name || field.id;
}

function getFieldDisplayValue(field: RequestFieldDto, request: Request): string {
    if (field.hidden) return "";

    if (field.type === RequestParameterTypeEnum.RADIO_BUTTON && field.group && request.itemsByGroup[field.group]) {
        const group = request.itemsByGroup[field.group];
        const gv = group.groupValue;
        if (!gv) return "-";
        const selected = group.children.map((c) => c.field).find((f) => f?.key === gv);
        return selected?.parameterText ?? selected?.displayTitle ?? gv;
    }

    const v = field.paramValue;
    if (v === null || v === undefined || v === "") return "-";
    if (typeof v === "boolean") return v ? "Áno" : "Nie";
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
}

const quickInfoLabelSx = {
    letterSpacing: 0,
    color: "text.secondary",
} as const;

const quickInfoValueSx = (theme: Theme) => ({
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: 0,
    textAlign: "right" as const,
    [theme.breakpoints.down("sm")]: {
        textAlign: "left" as const,
    },
});

/** Label + value row: side-by-side on larger screens, stacked on small mobile. */
const labelValueRowSx = (theme: Theme) => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    [theme.breakpoints.down("sm")]: {
        flexDirection: "column",
        alignItems: "stretch",
        gap: theme.spacing(0.5),
    },
});

/** Section titles: `variant="h6"` (pkp 18 / 128 %) with bold override. */
const sectionHeadlineSx = (theme: Theme) => ({
    fontWeight: theme.typography.fontWeightBold,
    letterSpacing: 0,
});

/** Subheadline under "Obsah žiadosti" for each section group (one per titled block). */
const sectionSubheadlineSx = (theme: Theme) => ({
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: 0,
    color: theme.palette.text.primary,
    mt: theme.spacing(0.5),
    mb: theme.spacing(0.25),
});

export const SubmittedRequestDetailView = ({
    request,
    axiosInstance,
    requestModuleUrl,
    apiRouteConfig,
    downloadRequestDocument,
    downloadGeneratedSubmittedRequestDocument,
}: SubmittedRequestDetailViewProps) => {
    const theme = useTheme();
    const req = request.current;
    if (!req) return null;

    const states = req.requestState ?? [];
    const lastState = states.length > 0 ? states[states.length - 1] : null;
    const statusLabel = lastState?.description || lastState?.name || "-";
    const statusColor = getRequestStateLabelColor(theme, lastState?.requestStateTypeId ?? null);

    const hasGeneratedSubmittedRequestDocument = !!req.files?.some((f) => f?.taxonomy?.isRequestDocument === true);

    const attachmentFiles = (req.files ?? []).filter((f) => f?.taxonomy?.isRequestDocument !== true);

    const sortedFields = [...(req.fields ?? [])].sort((a, b) => a.order - b.order).filter((f) => !f.hidden);

    const sectionById = new Map<string, RequestParameterSectionDto>(
        (req.sections ?? []).map((s) => [s.id, s]),
    );

    const submittedContentFieldGroups = buildSubmittedContentFieldGroups(sortedFields, req, sectionById);
    const hasSubmittedContentFields = submittedContentFieldGroups.length > 0;
    const hasAttachmentsSection = attachmentFiles.length > 0;

    const handleDownloadRequestCopy = () => {
        if (hasGeneratedSubmittedRequestDocument) {
            downloadGeneratedSubmittedRequestDocument();
        } else {
            downloadRequestDocument();
        }
    };

    const SectionShell = ({
        children,
        showBorderBottom = true,
    }: {
        children: ReactNode;
        showBorderBottom?: boolean;
    }) => (
        <Box
            sx={{
                width: "100%",
                borderBottom: showBorderBottom ? `1px solid ${theme.palette.divider}` : "none",
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: "720px",
                    margin: "0 auto",
                    p: theme.spacing(3),
                    boxSizing: "border-box",
                }}
            >
                {children}
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "stretch",
                backgroundColor: theme.palette.common.white,
            }}
        >
            <SectionShell>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                        <Box
                            sx={{
                                width: theme.spacing(5.5),
                                height: theme.spacing(5.5),
                                borderRadius: 1.5,
                                bgcolor: HEADER_ICON_BG,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                            }}
                        >
                            <RequestTablePkIconSwitcher
                                materialIconName={req.icon}
                                width={26}
                                height={26}
                                color={theme.palette.common.white}
                            />
                        </Box>
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: theme.typography.fontWeightMedium,
                                letterSpacing: 0,
                                color: theme.palette.text.primary,
                            }}
                        >
                            {req.name ?? "Žiadosť"}
                        </Typography>
                    </Box>
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<DownloadFileIcon width={16} height={16} color={theme.palette.common.black} />}
                        onClick={handleDownloadRequestCopy}
                        sx={downloadCopyButtonSx(theme)}
                    >
                        Stiahnuť kópiu žiadosti
                    </Button>
                </Box>
            </SectionShell>

            <SectionShell>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                    <Box sx={labelValueRowSx(theme)}>
                        <Typography variant="body2" sx={quickInfoLabelSx}>
                            Stav žiadosti
                        </Typography>
                        <Typography variant="body2" sx={{ ...quickInfoValueSx(theme), color: statusColor }}>
                            {statusLabel}
                        </Typography>
                    </Box>
                    <Box sx={labelValueRowSx(theme)}>
                        <Typography variant="body2" sx={quickInfoLabelSx}>
                            Číslo žiadosti
                        </Typography>
                        <Typography variant="body2" sx={{ ...quickInfoValueSx(theme), color: "text.primary" }}>
                            {req.requestId ?? "-"}
                        </Typography>
                    </Box>
                </Box>
            </SectionShell>

            {(hasSubmittedContentFields || hasAttachmentsSection) && (
                <SectionShell showBorderBottom={false}>
                    {hasSubmittedContentFields && (
                        <>
                            <Typography variant="h6" component="p" sx={{ ...sectionHeadlineSx(theme), mb: theme.spacing(2) }}>
                                Obsah žiadosti
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(2.5) }}>
                                {submittedContentFieldGroups.map((group) => {
                                    const groupHasNonRadioFields = group.fields.some(
                                        (f) => f.type !== RequestParameterTypeEnum.RADIO_BUTTON,
                                    );
                                    const showGroupSubheadline = !!group.subheadline && groupHasNonRadioFields;

                                    return (
                                        <Box
                                            key={group.groupKey}
                                            sx={{ display: "flex", flexDirection: "column", gap: theme.spacing(1.5) }}
                                        >
                                            {showGroupSubheadline ? (
                                                <Typography variant="subtitle1" component="h3" sx={sectionSubheadlineSx(theme)}>
                                                    {group.subheadline}
                                                </Typography>
                                            ) : null}
                                            {group.fields.map((field) => {
                                                const value = getFieldDisplayValue(field, req);
                                                const rowLabel = getSubmittedFieldRowLabel(
                                                    field,
                                                    req,
                                                    sectionById,
                                                    groupHasNonRadioFields,
                                                );
                                                return (
                                                    <Box key={field.id} sx={labelValueRowSx(theme)}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                ...quickInfoLabelSx,
                                                                maxWidth: "48%",
                                                                [theme.breakpoints.down("sm")]: {
                                                                    maxWidth: "100%",
                                                                },
                                                            }}
                                                        >
                                                            {rowLabel}
                                                        </Typography>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                ...quickInfoValueSx(theme),
                                                                color: "text.primary",
                                                                maxWidth: "48%",
                                                                wordBreak: "break-word",
                                                                overflowWrap: "anywhere",
                                                                [theme.breakpoints.down("sm")]: {
                                                                    maxWidth: "100%",
                                                                    textAlign: "left",
                                                                },
                                                            }}
                                                        >
                                                            {value}
                                                        </Typography>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </>
                    )}
                    {hasAttachmentsSection && (
                        <Box sx={{ pt: 0, mt: hasSubmittedContentFields ? theme.spacing(3) : 0 }}>
                            <Typography variant="h6" component="p" sx={{ ...sectionHeadlineSx(theme), mb: theme.spacing(2) }}>
                                Priložené dokumenty
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                                {attachmentFiles.map((file) => {
                                    const taxonomyName = file.taxonomy?.name ?? "Príloha";
                                    return (
                                        <Box
                                            key={file.id}
                                            sx={{
                                                width: "100%",
                                                maxWidth: "720px",
                                                minHeight: theme.spacing(8),
                                                borderRadius: "7px",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: theme.spacing(2),
                                                py: theme.spacing(1.5),
                                                px: theme.spacing(2.5),
                                                boxSizing: "border-box",
                                                backgroundColor: theme.palette.grey[50],
                                                boxShadow: "none",
                                                [theme.breakpoints.down("sm")]: {
                                                    flexDirection: "column",
                                                    alignItems: "stretch",
                                                    justifyContent: "flex-start",
                                                },
                                            }}
                                        >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(0.75), minWidth: 0 }}>
                                                <DocumentListIcon width={16} height={16} color={theme.palette.primary.main} />
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: theme.typography.fontWeightRegular,
                                                        fontStyle: "normal",
                                                        letterSpacing: 0,
                                                        leadingTrim: "none",
                                                        color: theme.palette.grey[800],
                                                        overflow: "hidden",
                                                        textOverflow: "ellipsis",
                                                    }}
                                                >
                                                    {taxonomyName}
                                                </Typography>
                                            </Box>
                                            {file.id ? (
                                                <Button
                                                    variant="text"
                                                    color="primary"
                                                    startIcon={<DownloadIcon width={16} height={16} color={theme.palette.primary.main} />}
                                                    onClick={() => downloadStreamFile(axiosInstance, requestModuleUrl, file, apiRouteConfig)}
                                                    sx={{
                                                        ...attachmentDownloadButtonSx(theme),
                                                        [theme.breakpoints.down("sm")]: {
                                                            alignSelf: "flex-start",
                                                            pl: 0,
                                                        },
                                                    }}
                                                >
                                                    Stiahnuť
                                                </Button>
                                            ) : null}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </SectionShell>
            )}
        </Box>
    );
};
